import React, { useEffect, useState } from "react";
import api from "../api";
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldAlert, PlayCircle } from "lucide-react";

/**
 * Data Health Audit widget.
 *
 * Detects analyses whose stored Lighthouse / SEOZoom data are known to be
 * wrong (mock fallback OR wrong www/apex variant) and offers a one-click
 * re-queue. Prevents shipping wrong PageSpeed / authority numbers to clients
 * in the deep-analysis emails.
 */
export default function DataHealthAudit() {
  const [audit, setAudit] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requeuing, setRequeuing] = useState(false);
  const [resurrecting, setResurrecting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showSamples, setShowSamples] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/analyses/health-audit?limit=25"),
        api.get("/admin/queue/stuck?minutes=15"),
      ]);
      setAudit(a.data);
      setStuck(s.data);
    } catch (e) {
      setAudit({ error: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const requeue = async () => {
    if (!audit || audit.totalCompromised === 0) return;
    if (!window.confirm(
      `Riaccodare ${audit.totalCompromised} analisi compromesse?\n\n` +
      `Verranno rianalizzate in background. Ogni analisi costa qualche unit ` +
      `SEOZoom + una chiamata DataForSEO Lighthouse.`
    )) return;
    setRequeuing(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/analyses/requeue-compromised");
      setMsg({ ok: true, text: data.message });
      // Give the background tasks a couple of seconds to update `status:queued`
      // before re-fetching the audit.
      setTimeout(load, 3000);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setRequeuing(false);
    }
  };

  const resurrectStuck = async () => {
    if (!stuck || stuck.stuck === 0) return;
    if (!window.confirm(
      `Rimettere in coda ${stuck.stuck} analisi bloccate?\n\n` +
      `Verranno rieseguite in background. Nessun costo aggiuntivo se già ` +
      `chiamate esterne erano state pagate.`
    )) return;
    setResurrecting(true);
    setMsg(null);
    try {
      const { data } = await api.post("/admin/queue/resurrect-stuck");
      setMsg({ ok: true, text: data.message });
      setTimeout(load, 3000);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setResurrecting(false);
    }
  };

  if (loading && !audit) {
    return (
      <div className="card" data-testid="data-health-loading">
        <div className="flex items-center gap-2">
          <RefreshCw size={16} className="spin" />
          <span>Analisi coerenza dati in corso&hellip;</span>
        </div>
      </div>
    );
  }

  const compromised = audit?.totalCompromised ?? 0;
  const total = audit?.totalAnalyses ?? 0;
  const clean = compromised === 0;

  return (
    <div className="card" data-testid="data-health-audit">
      <div className="flex items-center gap-2 mb-2">
        {clean ? (
          <CheckCircle2 size={20} color="var(--mv-success)" />
        ) : (
          <ShieldAlert size={20} color="var(--mv-danger)" />
        )}
        <div className="h-section" style={{ margin: 0 }}>
          Coerenza dati analisi
        </div>
        <button
          onClick={load}
          className="btn btn-ghost"
          style={{ marginLeft: "auto", padding: "4px 8px" }}
          data-testid="data-health-refresh-btn"
          title="Ricalcola"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {audit?.error && (
        <div className="badge badge-red" data-testid="data-health-error">
          {audit.error}
        </div>
      )}

      {stuck && stuck.stuck > 0 && (
        <div className="mt-1 mb-3" style={{
          padding: 12, background: "#FEF3F2",
          border: "1px solid #FECDCA", borderRadius: 8
        }} data-testid="stuck-queue-alert">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} color="var(--mv-danger)" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <strong style={{ color: "#B42318" }}>
                {stuck.stuck} analisi bloccate in coda (&gt;{stuck.staleThresholdMin} min)
              </strong>
              <div className="text-small" style={{ color: "#7A271A", marginTop: 4 }}>
                Analisi in stato queued/running senza avanzamento — di solito segno
                di un pod K8s riavviato mentre stavano girando.
                Totale queued: <strong>{stuck.totalQueued}</strong> ·
                running: <strong>{stuck.totalRunning}</strong>
              </div>
              <button
                onClick={resurrectStuck}
                disabled={resurrecting}
                className="btn btn-primary mt-2"
                data-testid="resurrect-stuck-btn"
              >
                {resurrecting ? (
                  <><RefreshCw size={14} className="spin" /> Riaccodamento&hellip;</>
                ) : (
                  <><PlayCircle size={14} /> Sblocca {stuck.stuck} analisi</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {!audit?.error && (
        <>
          <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))" }}>
            <div style={{ padding: 12, background: "var(--mv-bg)", borderRadius: 8 }}>
              <div className="stat-label">Totale analisi</div>
              <div className="stat-num" data-testid="data-health-total">{total}</div>
            </div>
            <div style={{ padding: 12, background: clean ? "#EAF7EE" : "#FEF3F2", borderRadius: 8 }}>
              <div className="stat-label">Compromesse</div>
              <div className="stat-num"
                data-testid="data-health-compromised"
                style={{ color: clean ? "var(--mv-success)" : "var(--mv-danger)" }}>
                {compromised}
              </div>
            </div>
            <div style={{ padding: 12, background: "var(--mv-bg)", borderRadius: 8 }}>
              <div className="stat-label" title="Lighthouse ha ripiegato su mock deterministico">
                Lighthouse mock
              </div>
              <div className="stat-num" data-testid="data-health-lighthouse-mock">
                {audit?.lighthouseMock ?? 0}
              </div>
            </div>
            <div style={{ padding: 12, background: "var(--mv-bg)", borderRadius: 8 }}>
              <div className="stat-label" title="SEOZoom ha scelto la variante www con za=0 invece dell'apex">
                SEOZoom variante errata
              </div>
              <div className="stat-num" data-testid="data-health-seozoom-wrong">
                {audit?.seozoomWrongVariant ?? 0}
              </div>
            </div>
          </div>

          {clean ? (
            <div className="badge badge-green" data-testid="data-health-clean">
              Tutte le analisi risultano coerenti.
            </div>
          ) : (
            <>
              <div className="flex items-start gap-2 mb-3" style={{ padding: 12, background: "#FEF3F2", borderRadius: 8 }}>
                <AlertTriangle size={18} color="var(--mv-danger)" style={{ flexShrink: 0, marginTop: 2 }} />
                <div className="text-small">
                  Sono state trovate <strong>{compromised}</strong> analisi con dati
                  sospetti (Lighthouse mock e/o SEOZoom con variante www a zero).
                  {" "}Riaccodale prima di inviare nuove email ai clienti.
                </div>
              </div>

              <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
                <button
                  onClick={requeue}
                  disabled={requeuing}
                  className="btn btn-primary"
                  data-testid="data-health-requeue-btn"
                >
                  {requeuing ? (
                    <><RefreshCw size={16} className="spin" /> Riaccodamento&hellip;</>
                  ) : (
                    <><PlayCircle size={16} /> Riaccoda {compromised} analisi</>
                  )}
                </button>
                <button
                  onClick={() => setShowSamples(v => !v)}
                  className="btn btn-ghost"
                  data-testid="data-health-toggle-samples-btn"
                >
                  {showSamples ? "Nascondi dettaglio" : `Mostra prime ${audit?.samples?.length ?? 0}`}
                </button>
              </div>

              {msg && (
                <div className={`badge ${msg.ok ? "badge-green" : "badge-red"} mt-2`}
                  data-testid="data-health-requeue-msg">
                  {msg.text}
                </div>
              )}

              {showSamples && audit?.samples?.length > 0 && (
                <div className="mt-3" data-testid="data-health-samples">
                  <table style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: "left", borderBottom: "1px solid var(--mv-border)" }}>
                        <th style={{ padding: "6px 4px" }}>Azienda</th>
                        <th style={{ padding: "6px 4px" }}>Sito</th>
                        <th style={{ padding: "6px 4px" }}>Motivo</th>
                        <th style={{ padding: "6px 4px" }}>Perf M</th>
                        <th style={{ padding: "6px 4px" }}>ZA</th>
                        <th style={{ padding: "6px 4px" }}>Traffic</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.samples.map((s) => (
                        <tr key={s.listItemId} style={{ borderBottom: "1px dashed var(--mv-border)" }}>
                          <td style={{ padding: "6px 4px" }}>{s.companyName || "—"}</td>
                          <td style={{ padding: "6px 4px", color: "var(--mv-primary)" }}>{s.website || "—"}</td>
                          <td style={{ padding: "6px 4px", fontSize: 12 }}>
                            {s.reasons.map(r => (
                              <span key={r} className="badge badge-red" style={{ marginRight: 4 }}>
                                {r === "lighthouse_mock_mobile" && "LH mock M"}
                                {r === "lighthouse_mock_desktop" && "LH mock D"}
                                {r === "seozoom_wrong_variant" && "SZ www→0"}
                              </span>
                            ))}
                          </td>
                          <td style={{ padding: "6px 4px" }}>{s.perf_mobile ?? "—"}</td>
                          <td style={{ padding: "6px 4px" }}>{s.z_authority ?? "—"}</td>
                          <td style={{ padding: "6px 4px" }}>{s.estimated_traffic ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
